/* GENERATED FILE — DO NOT EDIT.
 *
 * Emitted from `contracts/openapi/policy.yaml` by `tools/openapi_ts/generate.py`.
 * Regenerate with `python -m tools.openapi_ts.generate`;
 * `test_generated_client_matches_the_contract` fails the build if this file drifts from the spec.
 *
 * Hard rule 10 makes the spec authoritative, so editing this file by hand would put the client and
 * the server behind two different sources of truth — which is the drift the rule exists to prevent.
 *
 * Each type has a matching `is<Name>` guard. The types are for `tsc`; the guards are because a
 * response is data from another process, and a compile-time type is only a claim about it.
 */


export interface PolicyBundle {
  readonly id: string;
  readonly name: string;
  readonly description: string;
}

export function isPolicyBundle(value: unknown): value is PolicyBundle {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['id'] === 'string' &&
    typeof candidate['name'] === 'string' &&
    typeof candidate['description'] === 'string'
  );
}

export interface PolicyBundlePage {
  readonly items: readonly PolicyBundle[];
}

export function isPolicyBundlePage(value: unknown): value is PolicyBundlePage {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    Array.isArray(candidate['items']) &&
    (candidate['items'] as readonly unknown[]).every((item) => isPolicyBundle(item))
  );
}

export interface BundleVersion {
  readonly bundle_id: string;
  /**
   * `null` while DRAFT. An unpublished draft is not something an activation can pin to, and a
   * `0.0.0` placeholder would be a version number that looks real.
   */
  readonly semver?: string | null;
  readonly state: 'draft' | 'active' | 'deprecated';
  /**
   * Where the bytes live. Content-addressed from the hash, so re-uploading identical bytes writes
   * the same key rather than accumulating copies.
   */
  readonly artifact_ref: string;
  /**
   * Verified against the fetched bytes before anything is instantiated. Object storage is not
   * trusted to return what was written: a substituted artifact would otherwise be a silent policy
   * change with no audit record.
   */
  readonly content_hash: string;
  readonly byte_length: number;
  readonly created_by: string;
  readonly created_at: string;
  readonly rego_source_ref?: string | null;
}

export function isBundleVersion(value: unknown): value is BundleVersion {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['bundle_id'] === 'string' &&
    (['draft', 'active', 'deprecated'] as readonly unknown[]).includes(candidate['state']) &&
    typeof candidate['artifact_ref'] === 'string' &&
    typeof candidate['content_hash'] === 'string' &&
    typeof candidate['byte_length'] === 'number' &&
    typeof candidate['created_by'] === 'string' &&
    typeof candidate['created_at'] === 'string'
  );
}

export interface BundleVersionPage {
  readonly items: readonly BundleVersion[];
}

export function isBundleVersionPage(value: unknown): value is BundleVersionPage {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    Array.isArray(candidate['items']) &&
    (candidate['items'] as readonly unknown[]).every((item) => isBundleVersion(item))
  );
}

export interface LoadedBundle {
  readonly bundle_name: string;
  /**
   * So a client can link this running snapshot to the stored bundle it came from without matching
   * on a name.
   */
  readonly bundle_id: string;
  /** The hash of the bytes this process is running, which is the point of the view. */
  readonly content_hash: string;
  /**
   * Which version these bytes are. Previously unanswerable from this view: a reader could see that
   * something was running and not which version, so "is the process on the version I activated"
   * needed the stored list and a hash comparison.
   */
  readonly semver: string;
  /**
   * When this process last re-read the bundle from the database. With `max_staleness_seconds` this
   * makes the staleness window a figure a reader can check rather than a promise.
   */
  readonly loaded_at: string;
}

export function isLoadedBundle(value: unknown): value is LoadedBundle {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['bundle_name'] === 'string' &&
    typeof candidate['bundle_id'] === 'string' &&
    typeof candidate['content_hash'] === 'string' &&
    typeof candidate['semver'] === 'string' &&
    typeof candidate['loaded_at'] === 'string'
  );
}

export interface LoadedBundlePage {
  readonly items: readonly LoadedBundle[];
  readonly meta: Readonly<Record<string, unknown>>;
}

export function isLoadedBundlePage(value: unknown): value is LoadedBundlePage {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    Array.isArray(candidate['items']) &&
    (candidate['items'] as readonly unknown[]).every((item) => isLoadedBundle(item)) &&
    candidate['meta'] !== undefined
  );
}

/** Whether the calling tenant enforces attribute-based access control, and whose answer it is. */
export interface AbacPosture {
  /** The effective answer for this tenant. */
  readonly enabled: boolean;
  /**
   * True when a tenant-level setting exists. False means `deployment_default` is answering, which
   * is the state of every tenant on an install where nobody has configured one.
   */
  readonly declared: boolean;
  /**
   * `LUMIQ_ABAC_ENABLED` for this process — what an undeclared tenant inherits. Carried so an
   * undeclared answer can explain where it came from rather than reading as a choice.
   */
  readonly deployment_default: boolean;
  readonly mode: 'rbac_only' | 'rbac_and_attribute_policy';
  /**
   * Who set this, as the subject string their token carried. Null when undeclared — there is
   * nobody to name, and reporting an actor for a defaulted answer would attribute a decision
   * nobody made.
   */
  readonly updated_by?: string | null;
  /** When it was set. Null when undeclared. */
  readonly updated_at?: string | null;
}

export function isAbacPosture(value: unknown): value is AbacPosture {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['enabled'] === 'boolean' &&
    typeof candidate['declared'] === 'boolean' &&
    typeof candidate['deployment_default'] === 'boolean' &&
    (['rbac_only', 'rbac_and_attribute_policy'] as readonly unknown[]).includes(candidate['mode'])
  );
}

/** What was deleted, and what deleting it did not do. */
export interface BundleDeletion {
  readonly deleted: PolicyBundle;
  /**
   * Always true, and stated rather than left to the client because it is the one thing about this
   * operation a reader is most likely to get wrong. Deleting a bundle — including the governing
   * `authz` bundle — leaves the tenant's enforcement setting exactly as it was.
   */
  readonly abac_unchanged: boolean;
  /**
   * Host-written. Says which bundle was deleted and that enforcement is unchanged, so a client
   * that renders nothing else still cannot claim the opposite.
   */
  readonly note: string;
}

export function isBundleDeletion(value: unknown): value is BundleDeletion {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    candidate['deleted'] !== undefined &&
    typeof candidate['abac_unchanged'] === 'boolean' &&
    typeof candidate['note'] === 'string'
  );
}

/** Why attribute scoping is or is not deciding anything for the calling tenant. */
export interface EnforcementState {
  /**
   * The calling tenant's own policy state. * `no_bundle` — no governing bundle exists for this
   * tenant, so scoping does not run and roles decide alone. The **ordinary** state of a tenant
   * that has not adopted attribute-based policy, and not an error. * `no_active_version` — a
   * bundle exists with nothing active, so every request the roles would permit is refused. Fails
   * closed deliberately: deprecating the only active version must not be a way to switch scoping
   * off. * `load_failed` — an active version exists and cannot be loaded (hash mismatch, missing
   * artifact, a module that will not instantiate). Fails closed. * `loaded` — the bundle is in
   * this process and narrows every role grant it matches.
   */
  readonly state: 'no_bundle' | 'no_active_version' | 'load_failed' | 'loaded';
  /**
   * One sentence a reader can act on. **Host-written from the failure's type, never from an engine
   * or driver message** — those carry artifact refs, paths and statements, and this is readable by
   * anyone holding `policy.bundle:read`.
   */
  readonly detail: string;
  /** The loaded version, or null when nothing is loaded. */
  readonly semver?: string | null;
  /** The hash of the bytes loaded, or null. */
  readonly content_hash?: string | null;
  /** When this process last loaded it, or null. */
  readonly loaded_at?: string | null;
  /** The governing bundle name this state is about. */
  readonly bundle_name: string;
  /** A WASM engine is installed in this process. */
  readonly runtime_available: boolean;
  /**
   * **Whether attribute scoping is switched on for the calling tenant**, which is the tenant's own
   * explicit setting or the deployment default it inherits. This field used to be the deployment
   * flag, so every tenant on a shared install was told the same thing. The value is unchanged on
   * any deployment where the flag is still the only input; what changed is that a tenant with its
   * own setting now gets its own answer. `evaluator_wired` carries the old fact under a name that
   * says what it is.
   */
  readonly abac_enabled: boolean;
  /**
   * True when this tenant has an explicit setting, false when the deployment default is answering.
   * "Off because nobody configured this" and "off because an administrator decided" are different
   * states and a screen must be able to say which.
   */
  readonly abac_declared: boolean;
  /**
   * The policy evaluator is wired into authorization in this process — the deployment-level fact
   * `abac_enabled` used to carry. False here means no tenant's setting can take effect, whatever
   * it says.
   */
  readonly evaluator_wired: boolean;
  /**
   * What decides a request for this tenant. **Two values and not three**: "roles plus a policy
   * that is currently refusing everything" is still the second mode — the policy *is* deciding,
   * and what it has decided is to refuse. A third value would let a client report an enforcing
   * deployment as unenforced on exactly the day it starts refusing.
   */
  readonly mode: 'rbac_only' | 'rbac_and_attribute_policy';
  readonly posture: AbacPosture;
  /**
   * True only when an engine is installed, the evaluator is wired, **this tenant's setting is
   * on**, and `state` is `loaded`. The single field a client should render "enforcing" from.
   */
  readonly enforcing: boolean;
}

export function isEnforcementState(value: unknown): value is EnforcementState {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    (['no_bundle', 'no_active_version', 'load_failed', 'loaded'] as readonly unknown[]).includes(candidate['state']) &&
    typeof candidate['detail'] === 'string' &&
    typeof candidate['bundle_name'] === 'string' &&
    typeof candidate['runtime_available'] === 'boolean' &&
    typeof candidate['abac_enabled'] === 'boolean' &&
    typeof candidate['abac_declared'] === 'boolean' &&
    typeof candidate['evaluator_wired'] === 'boolean' &&
    (['rbac_only', 'rbac_and_attribute_policy'] as readonly unknown[]).includes(candidate['mode']) &&
    candidate['posture'] !== undefined &&
    typeof candidate['enforcing'] === 'boolean'
  );
}

/**
 * One entry from the append-only activation log — the record an auditor reads to answer "what was
 * permitted on 3 March".
 */
export interface Activation {
  /** The version that became active. */
  readonly semver: string;
  /**
   * What it replaced, or null for the first activation. Null is "there was nothing before", which
   * is a different fact from an omitted field.
   */
  readonly previous_semver?: string | null;
  /**
   * The hash of the bytes that became active, denormalised onto the log on purpose: reading it
   * through the version row after a rollback answers a different question — that version's
   * *current* hash rather than the one that was live at this instant.
   */
  readonly content_hash: string;
  /**
   * Who activated it, as the subject from their verified token. A string rather than a UUID
   * because OIDC defines `sub` as a StringOrURI.
   */
  readonly actor: string;
  readonly activated_at: string;
}

export function isActivation(value: unknown): value is Activation {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['semver'] === 'string' &&
    typeof candidate['content_hash'] === 'string' &&
    typeof candidate['actor'] === 'string' &&
    typeof candidate['activated_at'] === 'string'
  );
}

export interface ActivationPage {
  readonly items: readonly Activation[];
  readonly meta: Readonly<Record<string, unknown>>;
}

export function isActivationPage(value: unknown): value is ActivationPage {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    Array.isArray(candidate['items']) &&
    (candidate['items'] as readonly unknown[]).every((item) => isActivation(item)) &&
    candidate['meta'] !== undefined
  );
}

/**
 * One row of the builder: who, doing what, to what, and whether it is permitted. There is
 * deliberately no column, attribute or field. This is role-level ABAC, and a field here would be
 * the first half of a masking feature the platform is not wired for.
 */
export interface PolicyRule {
  /**
   * A role name this tenant has. One it does not have is refused: the rule would compile, load and
   * match nothing. Letters, digits, space, dot, underscore, colon and hyphen, up to 64 characters
   * — a name outside that cannot be written into a policy safely.
   */
  readonly role: string;
  /**
   * An action the resource type governs, from `PolicyVocabulary.resources`. One that does not
   * apply is refused, because no request will ever carry it.
   */
  readonly action: string;
  /** A governed resource type, from `PolicyVocabulary.resources`. */
  readonly resource_type: string;
  /**
   * **Deny wins.** Within a policy, `allow` is true only when some allow rule matches and no deny
   * rule does — so a broad allow can be narrowed by a specific deny. Two rules with the same role,
   * action and resource but opposite effects are refused rather than resolved: one of them would
   * do nothing and which one is not visible on the form.
   */
  readonly effect: 'allow' | 'deny';
  /**
   * One resource, or **null for every resource of that type**. The two are different policies
   * rather than a convenience: null is how "stewards may read datasets" is expressed, and it also
   * matches a collection action, where the resource id is absent.
   */
  readonly resource_id: string | null;
}

export function isPolicyRule(value: unknown): value is PolicyRule {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['role'] === 'string' &&
    typeof candidate['action'] === 'string' &&
    typeof candidate['resource_type'] === 'string' &&
    (['allow', 'deny'] as readonly unknown[]).includes(candidate['effect']) &&
    (typeof candidate['resource_id'] === 'string' || candidate['resource_id'] === null)
  );
}

/**
 * A whole policy as an ordered list of rules — what the builder posts. Order is the author's and
 * does not change the outcome: allow rules OR together, deny rules OR together, and deny always
 * wins. Two documents differing only in order are semantically identical and will still produce
 * different bundles, because the document did change.
 */
export interface PolicyDocument {
  /**
   * At least one, and at most `PolicyVocabulary.max_rules`. An empty policy is refused: it would
   * refuse every request the roles permit, which is what deprecating the only active version
   * already does, deliberately and visibly.
   */
  readonly rules: readonly PolicyRule[];
}

export function isPolicyDocument(value: unknown): value is PolicyDocument {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    Array.isArray(candidate['rules']) &&
    (candidate['rules'] as readonly unknown[]).every((item) => isPolicyRule(item))
  );
}

/** One reason a policy will not save, attributed to the rule it belongs to. */
export interface PolicyProblem {
  /**
   * Stable and machine-readable, so a form can group or filter. `rule_invalid`, `rule_malformed`,
   * `rule_unknown_reference`, `policy_invalid`, `policy_malformed`, and
   * `generated_policy_invalid`.
   */
  readonly code: string;
  /** Written for the person holding the form, not for a log. */
  readonly message: string;
  /**
   * 1-based, matching the form's own row numbers, or **null for a problem that belongs to no row**
   * — no rules at all, too many, or a body that is not a policy. Attributing one of those to rule
   * 1 would put a marker on a rule that is fine. Null also for `generated_policy_invalid`, which
   * is a fault in the platform's generator rather than in any rule the author wrote; the message
   * says so.
   */
  readonly rule_number: number | null;
}

export function isPolicyProblem(value: unknown): value is PolicyProblem {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['code'] === 'string' &&
    typeof candidate['message'] === 'string' &&
    (typeof candidate['rule_number'] === 'number' || candidate['rule_number'] === null)
  );
}

/**
 * The result of checking a policy. `valid` is authoritative; an empty `problems` list on its own
 * does not distinguish a sound policy from a request that failed.
 */
export interface PolicyValidation {
  readonly valid: boolean;
  readonly problems: readonly PolicyProblem[];
}

export function isPolicyValidation(value: unknown): value is PolicyValidation {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['valid'] === 'boolean' &&
    Array.isArray(candidate['problems']) &&
    (candidate['problems'] as readonly unknown[]).every((item) => isPolicyProblem(item))
  );
}

/** One governed resource type and the actions it has. */
export interface ResourceActions {
  readonly resource_type: string;
  readonly actions: readonly string[];
}

export function isResourceActions(value: unknown): value is ResourceActions {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['resource_type'] === 'string' &&
    Array.isArray(candidate['actions']) &&
    (candidate['actions'] as readonly unknown[]).every((item) => typeof item === 'string')
  );
}

/** What a rule may be built from on this deployment, for this tenant. */
export interface PolicyVocabulary {
  /**
   * This tenant's own role names, sorted. **An empty array is a real answer**: a tenant with no
   * roles cannot have a policy that decides anything, because every rule would name a role nobody
   * holds. A client should say that rather than showing an empty dropdown.
   */
  readonly roles: readonly string[];
  /** Always `allow` and `deny`, served so a client need not hardcode them. */
  readonly effects: readonly string[];
  /**
   * Every governed resource type with its actions, from the platform's own permission catalogue —
   * so it cannot offer an action no `ensure_permitted` call carries.
   */
  readonly resources: readonly ResourceActions[];
  /**
   * How many rules one policy may carry. A refusal rather than a truncation: a policy silently
   * missing a rule permits or refuses something nobody reviewed.
   */
  readonly max_rules: number;
  /**
   * Whether this deployment can compile a policy at all. False means the authoring writes answer
   * 503, and a client should not offer a builder.
   */
  readonly compiler_available: boolean;
}

export function isPolicyVocabulary(value: unknown): value is PolicyVocabulary {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    Array.isArray(candidate['roles']) &&
    (candidate['roles'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    Array.isArray(candidate['effects']) &&
    (candidate['effects'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    Array.isArray(candidate['resources']) &&
    (candidate['resources'] as readonly unknown[]).every((item) => isResourceActions(item)) &&
    typeof candidate['max_rules'] === 'number' &&
    typeof candidate['compiler_available'] === 'boolean'
  );
}

/**
 * Whether a draft was written, and the reasons if it was not. Exactly one of `version` and a
 * non-empty `problems` is present. **Branch on `stored`**, not on either field, so a client cannot
 * read an empty problems list as success.
 */
export interface AuthoringResult {
  readonly stored: boolean;
  /** The stored draft, or null when the policy was refused. */
  readonly version: BundleVersion | null;
  readonly problems: readonly PolicyProblem[];
}

export function isAuthoringResult(value: unknown): value is AuthoringResult {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['stored'] === 'boolean' &&
    (candidate['version'] === null || isBundleVersion(candidate['version'])) &&
    Array.isArray(candidate['problems']) &&
    (candidate['problems'] as readonly unknown[]).every((item) => isPolicyProblem(item))
  );
}

/**
 * The rules a version was built from and the policy they generated. `authored_here` false with
 * both nulls is an ordinary state — the version was uploaded as a pre-compiled artifact.
 */
export interface VersionPolicy {
  readonly semver: string;
  readonly authored_here: boolean;
  /** The rules to reopen in the builder, or null. */
  readonly rules: readonly PolicyRule[] | null;
  /**
   * The generated policy **as it was compiled**, for a read-only advanced view. Not regenerated
   * from the rules: the current generator might render them differently from whatever produced the
   * module this version activates. Nothing in the normal workflow needs this.
   */
  readonly rego: string | null;
}

export function isVersionPolicy(value: unknown): value is VersionPolicy {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['semver'] === 'string' &&
    typeof candidate['authored_here'] === 'boolean' &&
    candidate['rules'] !== undefined &&
    (typeof candidate['rego'] === 'string' || candidate['rego'] === null)
  );
}
